// Catalog Shell - Grid Surface Pane
// A container pane that arranges child panes in a CSS Grid with resizable cells
// ============================================================================

import {
    PaneType,
    PaneInstance,
    PaneInstanceSpec,
    PaneTypeRenderContext,
    SurfaceAPI,
    GridLayoutConfig,
    GridCellConfig,
    OpenPaneOptions,
    CellPosition,
    generateId,
    DragData,
    DropTarget,
} from '../core/types';
import { Shell } from '../core/shell';
import { createElement, clearElement, findAncestorByClass } from '../utils/dom';
import { createButton } from '../utils/pane-ui';
import { icons } from '../utils/icons';

// ============================================================================
// Types
// ============================================================================

interface GridCell {
    cellId: string;
    rowStart: number;
    rowEnd: number;
    colStart: number;
    colEnd: number;
    panes: PaneInstance[];
    activePaneIndex: number;
    element?: HTMLElement;
    tabBarElement?: HTMLElement;
    contentElement?: HTMLElement;
}

interface ResizeState {
    type: 'column' | 'row';
    index: number;
    startPos: number;
    startSizes: number[];
    snapPoints: number[];  // Calculated even-distribution snap points for this resize
    isSnapped: boolean;    // Whether currently snapped
}

interface DragState {
    draggedPaneId: string;
    sourceCellId: string;
    dragElement: HTMLElement | null;
}

// ============================================================================
// Grid Surface Implementation
// ============================================================================

export class GridSurface implements SurfaceAPI {
    public readonly surfaceId: string;
    public parentSurface?: SurfaceAPI;
    private shell: Shell;
    private instance: PaneInstance;
    private container: HTMLElement;

    // Layout state
    private columnCount: number = 1;
    private columnSizes: number[] = [100];
    private rowSizes: number[] = [100];
    private cells: Map<string, GridCell> = new Map();

    // DOM elements
    private gridElement: HTMLElement | null = null;
    private columnDividers: (HTMLElement | null)[] = [];
    private rowDividers: (HTMLElement | null)[] = [];

    // Detached panes — removed from cells but kept alive for re-attachment
    private detachedPanes: Map<string, PaneInstance> = new Map(); // keyed by typeId
    private detachedSourceCells: Map<string, string> = new Map(); // typeId → source cellId

    // Resize state
    private resizeState: ResizeState | null = null;
    private minCellSize: number = 80;
    private snapTolerance: number = 1;  // Percentage tolerance for snapping (0 to disable)
    private snapIndicator: HTMLElement | null = null;

    // Drag state
    private dragState: DragState | null = null;
    private dragHoldTimeout: ReturnType<typeof setTimeout> | null = null;
    private dragHoldMouseUp: (() => void) | null = null;
    private dragHoldMouseMove: ((e: MouseEvent) => void) | null = null;
    private readonly DRAG_HOLD_TIME = 150;

    // Context menu
    private contextMenuElement: HTMLElement | null = null;

    // Configured pane types for this surface (from config.configuredPaneTypes)
    private configuredPaneTypes: Set<string> = new Set();

    // Surface modal state
    private modalOverlay: HTMLElement | null = null;
    private modalContent: HTMLElement | null = null;
    private modalActive: boolean = false;

    constructor(shell: Shell, instance: PaneInstance, container: HTMLElement) {
        this.surfaceId = instance.instanceId;
        this.shell = shell;
        this.instance = instance;
        this.container = container;

        // Register this surface with the shell
        shell.registerSurface(this);
    }

    // ========================================================================
    // SurfaceAPI Implementation
    // ========================================================================

    getSurfaceType(): 'grid' {
        return 'grid';
    }

    getPanes(): PaneInstance[] {
        const panes: PaneInstance[] = [];
        for (const cell of this.cells.values()) {
            panes.push(...cell.panes);
        }
        return panes;
    }

    getActivePane(): PaneInstance | null {
        for (const cell of this.cells.values()) {
            if (cell.panes.length > 0 && cell.activePaneIndex >= 0) {
                return cell.panes[cell.activePaneIndex] ?? null;
            }
        }
        return null;
    }

    setActivePane(instanceId: string): void {
        for (const cell of this.cells.values()) {
            const index = cell.panes.findIndex(p => p.instanceId === instanceId);
            if (index >= 0) {
                cell.activePaneIndex = index;
                this.renderCellContent(cell);
                this.updateTabStyles(cell);
                this.shell.setFocusedPane(instanceId, this.surfaceId);
                return;
            }
        }
    }

    openPane(spec: PaneInstanceSpec, options?: OpenPaneOptions): PaneInstance {
        const instance = this.shell.getTypeRegistry().createInstance(spec);
        instance.parentSurfaceId = this.surfaceId;
        instance.isDynamic = true;
        this.shell.registerPaneInstance(instance);

        // Target cell: explicit position > the origin's cell (or its right-hand
        // neighbour for placement 'adjacent') > the focused pane's cell > the
        // first cell. Opening "beside where I clicked" is what a caller passing
        // originPaneId means, and it must never disturb an unrelated cell.
        let targetCell: GridCell | undefined;
        if (options?.position) {
            targetCell = this.findCellAtPosition(options.position);
        }
        const originCell = options?.originPaneId ? this.findCellForPane(options.originPaneId) : undefined;
        if (!targetCell && originCell) {
            targetCell = options?.placement === 'adjacent'
                ? this.findCellRightOf(originCell) ?? originCell
                : originCell;
        }
        if (!targetCell) {
            const focused = this.shell.getFocusedPane();
            if (focused) targetCell = this.findCellForPane(focused.instanceId);
        }
        if (!targetCell) {
            targetCell = this.cells.values().next().value;
        }

        if (targetCell) {
            // Replace existing pane of the same typeId (prevents tab accumulation)
            const existingIndex = targetCell.panes.findIndex(p => p.typeId === spec.typeId);
            if (existingIndex >= 0) {
                const [existing] = targetCell.panes.splice(existingIndex, 1);
                this.shell.unregisterPaneInstance(existing.instanceId);
                targetCell.panes.splice(existingIndex, 0, instance);
                if (options?.activate !== false) {
                    targetCell.activePaneIndex = existingIndex;
                }
            } else {
                // Right after the origin's tab when it lives in this cell; else last.
                const originIndex = targetCell === originCell && options?.originPaneId
                    ? targetCell.panes.findIndex(p => p.instanceId === options.originPaneId)
                    : -1;
                const insertAt = originIndex >= 0 ? originIndex + 1 : targetCell.panes.length;
                targetCell.panes.splice(insertAt, 0, instance);
                if (options?.activate !== false) {
                    targetCell.activePaneIndex = insertAt;
                }
            }
            this.renderCellContent(targetCell);
            this.renderCellTabBar(targetCell);

            if (options?.activate !== false) {
                this.shell.setFocusedPane(instance.instanceId, this.surfaceId);
            }
        }

        this.shell.emit('pane-opened', { instanceId: instance.instanceId, surfaceId: this.surfaceId });
        this.shell.saveConfiguration();
        return instance;
    }

    closePane(instanceId: string): void {
        for (const cell of this.cells.values()) {
            const index = cell.panes.findIndex(p => p.instanceId === instanceId);
            if (index >= 0) {
                const [removed] = cell.panes.splice(index, 1);

                // Adjust active index
                if (cell.activePaneIndex >= cell.panes.length) {
                    cell.activePaneIndex = Math.max(0, cell.panes.length - 1);
                }

                // Unregister instance
                this.shell.unregisterPaneInstance(instanceId);
                this.shell.emit('pane-closed', { instanceId, surfaceId: this.surfaceId });

                // Re-render
                this.renderCellContent(cell);
                this.renderCellTabBar(cell);

                // The closed pane may have been the focused one: hand focus to
                // the cell's new active pane (or clear it) so the action bar and
                // title rows do not keep describing a pane that no longer exists.
                if (!this.shell.getFocusedPane()) {
                    const next = cell.panes[cell.activePaneIndex];
                    this.shell.setFocusedPane(next?.instanceId ?? null, next ? this.surfaceId : null);
                }
                this.shell.saveConfiguration();
                return;
            }
        }
    }

    /**
     * Run the pane type's confirmClose guard (if any) ahead of a user-initiated
     * close. Returns true when the close should proceed.
     */
    private async shouldClosePane(pane: PaneInstance): Promise<boolean> {
        const type = this.shell.getPaneType(pane.typeId);
        if (type?.confirmClose) {
            return await type.confirmClose(pane);
        }
        return true;
    }

    getPaneById(instanceId: string): PaneInstance | null {
        for (const cell of this.cells.values()) {
            const pane = cell.panes.find(p => p.instanceId === instanceId);
            if (pane) return pane;
        }
        return null;
    }

    /**
     * Update a pane's title and refresh only the affected tab element.
     */
    updatePaneTitle(instanceId: string, title: string): void {
        const pane = this.getPaneById(instanceId);
        if (!pane) return;
        pane.title = title;
        const tab = this.container.querySelector(`.cs-cell-tab[data-instance-id="${instanceId}"]`);
        if (tab) {
            tab.textContent = title;
        }
    }

    // ========================================================================
    // Surface Modal
    // ========================================================================

    /**
     * Show a modal overlay on the surface.
     * Blurs and darkens the underlying grid panes. The provided element
     * is displayed centered in the overlay. Call hideModal() to dismiss.
     */
    showModal(element: HTMLElement): void {
        if (this.modalActive) {
            // Replace content if already showing
            if (this.modalContent) {
                this.modalContent.innerHTML = '';
                this.modalContent.appendChild(element);
            }
            return;
        }

        this.modalActive = true;

        // Apply blur to grid
        if (this.gridElement) {
            this.gridElement.classList.add('cs-grid-surface--blurred');
        }

        // Create overlay
        this.modalOverlay = createElement('div', {
            className: 'cs-surface-modal-overlay',
        });

        this.modalContent = createElement('div', {
            className: 'cs-surface-modal-content',
        });
        this.modalContent.appendChild(element);
        this.modalOverlay.appendChild(this.modalContent);
        this.container.appendChild(this.modalOverlay);
    }

    /**
     * Hide the surface modal and restore normal pane display.
     */
    hideModal(): void {
        if (!this.modalActive) return;

        this.modalActive = false;

        // Remove blur from grid
        if (this.gridElement) {
            this.gridElement.classList.remove('cs-grid-surface--blurred');
        }

        // Remove overlay
        if (this.modalOverlay) {
            this.modalOverlay.remove();
            this.modalOverlay = null;
            this.modalContent = null;
        }
    }

    /**
     * Whether the surface modal is currently shown.
     */
    isModalActive(): boolean {
        return this.modalActive;
    }

    // ========================================================================
    // Layout Initialization
    // ========================================================================

    initializeLayout(config: GridLayoutConfig): void {
        this.columnCount = config.columnCount;
        this.columnSizes = [...config.columnSizes];
        this.rowSizes = [...config.rowSizes];

        // Create cells from config
        this.cells.clear();
        for (const cellConfig of config.cells) {
            const cell = this.createCellFromConfig(cellConfig);
            this.cells.set(cell.cellId, cell);
        }

        // Set configured pane types from config
        if (config.configuredPaneTypes) {
            for (const typeId of config.configuredPaneTypes) {
                this.configuredPaneTypes.add(typeId);
            }
        }

        this.render();
    }

    private createCellFromConfig(config: GridCellConfig): GridCell {
        const panes: PaneInstance[] = [];

        for (const paneSpec of config.panes) {
            // If the pane spec has a nested layout config, pass it via context
            // so that container panes can restore their internal layout
            const specWithLayout = { ...paneSpec };
            if (paneSpec.nestedLayoutConfig) {
                specWithLayout.context = {
                    ...paneSpec.context,
                    __nestedLayoutConfig: paneSpec.nestedLayoutConfig,
                };
            }

            const instance = this.shell.getTypeRegistry().createInstance(specWithLayout);
            instance.parentSurfaceId = this.surfaceId;
            this.shell.registerPaneInstance(instance);
            panes.push(instance);
        }

        return {
            cellId: config.cellId || generateId('cell'),
            rowStart: config.rowStart,
            rowEnd: config.rowEnd,
            colStart: config.colStart,
            colEnd: config.colEnd,
            panes,
            activePaneIndex: config.activePaneIndex,
        };
    }

    // ========================================================================
    // Dynamic Columns (single-row strips)
    // ========================================================================
    //
    // Add/remove a full-height column at runtime, evening out the remaining
    // widths. Existing cells keep their live pane instances — render() reuses
    // each pane's cached `element` — so their DOM and state survive the reflow.
    // Intended for a side-by-side strip (e.g. a multiview of editors): a card
    // can open or close without tearing down the columns that stay.

    /**
     * Append a full-height column on the right holding `spec`, then even out all
     * column widths. Returns the new pane instance.
     */
    addColumn(spec: PaneInstanceSpec): PaneInstance {
        const instance = this.shell.getTypeRegistry().createInstance(spec);
        instance.parentSurfaceId = this.surfaceId;
        // The same gesture as openPane: a column the user added is one the
        // user can close.
        instance.isDynamic = true;
        this.shell.registerPaneInstance(instance);

        const newCol = this.columnSizes.length;
        this.columnSizes.push(0);
        this.columnCount = this.columnSizes.length;
        this.evenColumnSizes();

        const cell: GridCell = {
            cellId: generateId('cell'),
            rowStart: 0,
            rowEnd: this.rowSizes.length,
            colStart: newCol,
            colEnd: newCol + 1,
            panes: [instance],
            activePaneIndex: 0,
        };
        this.cells.set(cell.cellId, cell);

        this.render();
        this.shell.saveConfiguration();
        return instance;
    }

    /**
     * Remove the full-height column whose cell holds `instanceId`, destroying
     * that pane, then even out the remaining columns. Other cells' panes are
     * preserved. No-op when the pane isn't found or it is the only column.
     */
    removeColumn(instanceId: string): void {
        let target: GridCell | undefined;
        for (const c of this.cells.values()) {
            if (c.panes.some(p => p.instanceId === instanceId)) { target = c; break; }
        }
        if (!target || this.cells.size <= 1) return;

        for (const p of target.panes) this.shell.unregisterPaneInstance(p.instanceId);
        this.removeColumnTrack(target);
    }

    /**
     * Fold away every cell that holds no panes, as long as another cell is
     * left. The tab's × deliberately leaves an emptied cell behind as a drop
     * target (the grid's own "Remove" verb is what folds one); a surface
     * whose panes are whole columns calls this after `pane-closed` so the
     * column collapses instead of staying as an empty half. A full-height
     * single-column cell closes its column track; any other shape is merged
     * into a neighbour as "Remove This Cell" would.
     */
    removeEmptyCells(): void {
        for (const cell of Array.from(this.cells.values())) {
            if (this.cells.size <= 1) return;
            if (cell.panes.length > 0 || !this.cells.has(cell.cellId)) continue;
            const fullHeightColumn = cell.rowStart === 0 && cell.rowEnd === this.rowSizes.length
                && cell.colEnd - cell.colStart === 1;
            if (fullHeightColumn) this.removeColumnTrack(cell);
            else this.removeCell(cell);
        }
    }

    /** Delete `target` and close its column track, sliding every cell to its right one left. */
    private removeColumnTrack(target: GridCell): void {
        const removedCol = target.colStart;
        this.cells.delete(target.cellId);

        for (const c of this.cells.values()) {
            if (c.colStart > removedCol) c.colStart -= 1;
            if (c.colEnd > removedCol) c.colEnd -= 1;
        }
        this.columnSizes.splice(removedCol, 1);
        this.columnCount = this.columnSizes.length;
        this.evenColumnSizes();

        this.render();
        this.shell.saveConfiguration();
    }

    private evenColumnSizes(): void {
        const n = this.columnSizes.length;
        const even = n > 0 ? 100 / n : 100;
        this.columnSizes = this.columnSizes.map(() => even);
    }

    // ========================================================================
    // Rendering
    // ========================================================================

    render(): void {
        clearElement(this.container);

        // Create grid container
        this.gridElement = createElement('div', {
            className: 'cs-grid-surface',
        });

        // Apply grid template
        this.updateGridTemplate();

        // Render cells
        for (const cell of this.cells.values()) {
            this.renderCell(cell);
        }

        // Render dividers
        this.renderDividers();

        this.container.appendChild(this.gridElement);

        // Setup event listeners
        this.setupEventListeners();
    }

    private updateGridTemplate(): void {
        if (!this.gridElement) return;

        // Create grid template columns with divider gaps
        const colTemplate = this.columnSizes
            .map((size, i) => {
                const fr = `${size}fr`;
                return i < this.columnSizes.length - 1 ? `${fr} 4px` : fr;
            })
            .join(' ');

        // Create grid template rows with divider gaps
        const rowTemplate = this.rowSizes
            .map((size, i) => {
                const fr = `${size}fr`;
                return i < this.rowSizes.length - 1 ? `${fr} 4px` : fr;
            })
            .join(' ');

        this.gridElement.style.display = 'grid';
        this.gridElement.style.gridTemplateColumns = colTemplate;
        this.gridElement.style.gridTemplateRows = rowTemplate;
        this.gridElement.style.height = '100%';
        this.gridElement.style.overflow = 'hidden';
    }

    private renderCell(cell: GridCell): void {
        if (!this.gridElement) return;

        // Calculate grid position (accounting for divider gaps)
        const colStart = cell.colStart * 2 + 1;
        const colEnd = cell.colEnd * 2;
        const rowStart = cell.rowStart * 2 + 1;
        const rowEnd = cell.rowEnd * 2;

        // Create cell element
        cell.element = createElement('div', {
            className: 'cs-grid-cell',
            attributes: {
                'data-cell-id': cell.cellId,
            },
        });

        cell.element.style.gridColumn = `${colStart} / ${colEnd}`;
        cell.element.style.gridRow = `${rowStart} / ${rowEnd}`;

        // Context menu: on the tab bar, or on the whole cell when empty (no panes)
        cell.element.addEventListener('contextmenu', (e) => {
            const inTabBar = cell.tabBarElement?.contains(e.target as Node);
            if (!inTabBar && cell.panes.length > 0) return;
            e.preventDefault();
            // Detect which pane tab was right-clicked (if any)
            const tabEl = findAncestorByClass(e.target as HTMLElement, 'cs-cell-tab');
            const pane = tabEl
                ? cell.panes.find(p => p.instanceId === (tabEl as HTMLElement).dataset.instanceId)
                : undefined;
            this.showContextMenu(e, cell, pane);
        });

        // Tab bar (appended first for DOM order, populated after content
        // so tabBarActions callbacks can depend on state created during render)
        cell.tabBarElement = createElement('div', {
            className: 'cs-cell-tab-bar',
        });
        cell.element.appendChild(cell.tabBarElement);

        // Content area
        cell.contentElement = createElement('div', {
            className: 'cs-cell-content',
        });
        cell.contentElement.addEventListener('scroll', (e) => {
            const target = e.target as HTMLElement;
            // Ignore scroll events from nested cells
            if (target !== cell.contentElement && target.closest('.cs-cell-content') !== cell.contentElement) return;
            cell.tabBarElement?.classList.toggle('cs-scrolled', target.scrollTop > 0);
        }, true);
        this.renderCellContent(cell);
        cell.element.appendChild(cell.contentElement);

        // Now populate the tab bar (after content render)
        this.renderCellTabBar(cell);

        // Drop zone indicator
        const dropZone = createElement('div', {
            className: 'cs-drop-zone',
        });
        cell.element.appendChild(dropZone);

        this.gridElement.appendChild(cell.element);
    }

    private renderCellTabBar(cell: GridCell): void {
        if (!cell.tabBarElement) return;
        clearElement(cell.tabBarElement);

        if (cell.panes.length === 0) {
            // Empty cell indicator
            const emptyLabel = createElement('div', {
                className: 'cs-empty-cell-label',
                textContent: 'Drop panes here',
            });
            cell.tabBarElement.appendChild(emptyLabel);
            return;
        }

        // Sort panes alphabetically by title for display
        const sortedPanes = [...cell.panes]
            .map((pane, originalIndex) => ({ pane, originalIndex }))
            .sort((a, b) => a.pane.title.localeCompare(b.pane.title));

        for (const { pane, originalIndex } of sortedPanes) {
            const isActive = originalIndex === cell.activePaneIndex;
            const isRecommended = this.isPaneRecommended(pane.typeId);

            const tab = createElement('div', {
                className: `cs-cell-tab ${isActive ? 'cs-active' : ''} ${!isRecommended ? 'cs-muted' : ''}`,
                textContent: pane.title,
                attributes: {
                    'data-instance-id': pane.instanceId,
                    draggable: 'false', // We handle drag manually
                },
            });

            // Close button for dynamically opened panes
            if (pane.isDynamic) {
                const closeBtn = createButton({
                    icon: icons.close(),
                    onClick: async (e) => {
                        e.stopPropagation();
                        if (await this.shouldClosePane(pane)) {
                            this.closePane(pane.instanceId);
                        }
                    },
                });
                closeBtn.title = 'Close';
                closeBtn.classList.add('cs-tab-close-btn');
                tab.appendChild(closeBtn);
            }

            // Popout icon inside active tab (hidden if already in a popout window)
            if (originalIndex === cell.activePaneIndex && !this.parentSurface) {
                const paneType = this.shell.getPaneType(pane.typeId);
                if (paneType?.canCopyToPopup !== false) {
                    const popoutBtn = createButton({
                        icon: icons.popout(),
                        onClick: (e) => {
                            e.stopPropagation();
                            // Measure cell size before detaching (element leaves DOM after re-render)
                            const cellRect = cell.element?.getBoundingClientRect();
                            const sourceSize = cellRect && cellRect.width > 0 && cellRect.height > 0
                                ? { width: Math.round(cellRect.width), height: Math.round(cellRect.height) }
                                : undefined;
                            // Remove pane from cell (switch to another or leave empty)
                            const paneIndex = cell.panes.findIndex(p => p.instanceId === pane.instanceId);
                            if (paneIndex >= 0) {
                                const [removed] = cell.panes.splice(paneIndex, 1);
                                if (cell.activePaneIndex >= cell.panes.length) {
                                    cell.activePaneIndex = Math.max(0, cell.panes.length - 1);
                                }
                                // Stash for re-attachment (keeps subscriptions alive)
                                removed.isDetached = true;
                                this.shell.getPaneType(removed.typeId)?.onDetach?.(removed);
                                this.detachedPanes.set(removed.typeId, removed);
                                this.detachedSourceCells.set(removed.typeId, cell.cellId);
                                this.renderCellTabBar(cell);
                                this.renderCellContent(cell);
                            }
                            (this.shell as Shell).getPopoutManager().popoutPane(pane, this, sourceSize, cell.cellId);
                        },
                    });
                    popoutBtn.title = 'Pop out';
                    popoutBtn.classList.add('cs-tab-popout-btn');
                    tab.appendChild(popoutBtn);
                }
            }

            // Click to activate
            tab.addEventListener('click', (e) => {
                e.stopPropagation();
                this.setActivePane(pane.instanceId);
            });

            // Mousedown for drag initiation
            tab.addEventListener('mousedown', (e) => {
                if (e.button !== 0) return; // Left click only
                e.stopPropagation(); // Prevent parent surfaces from also starting drag
                this.startDragHold(pane.instanceId, cell.cellId, e);
            });

            cell.tabBarElement.appendChild(tab);
        }

        // Render tab bar actions for the active pane
        this.renderTabBarActions(cell);
    }

    /**
     * Re-run the pane type's `tabBarActions` for the cell showing this pane.
     * Only the active pane of a cell has its actions in the tab bar, so an
     * inactive pane's refresh is a no-op — the tab bar is rebuilt when it is
     * activated anyway.
     */
    refreshPaneActions(instanceId: string): void {
        for (const cell of this.cells.values()) {
            const active = cell.panes[cell.activePaneIndex];
            if (active?.instanceId === instanceId) {
                this.renderTabBarActions(cell);
                return;
            }
        }
    }

    private renderTabBarActions(cell: GridCell): void {
        if (!cell.tabBarElement) return;

        // Remove existing actions container
        const existing = cell.tabBarElement.querySelector('.cs-tab-bar-actions');
        if (existing) existing.remove();

        const activePane = cell.panes[cell.activePaneIndex];
        if (!activePane) return;

        const type = this.shell.getPaneType(activePane.typeId);
        const actions = type?.tabBarActions?.(activePane);
        if (!actions || actions.length === 0) return;

        const actionsContainer = createElement('div', {
            className: 'cs-tab-bar-actions',
        });
        for (const action of actions) {
            actionsContainer.appendChild(action);
        }
        cell.tabBarElement.appendChild(actionsContainer);
    }

    private renderCellContent(cell: GridCell): void {
        if (!cell.contentElement) return;
        clearElement(cell.contentElement);
        cell.tabBarElement?.classList.remove('cs-scrolled');

        if (cell.panes.length === 0 || cell.activePaneIndex < 0) {
            return;
        }

        const activePane = cell.panes[cell.activePaneIndex];
        if (!activePane) return;

        const type = this.shell.getPaneType(activePane.typeId);
        if (!type) {
            console.error(`Unknown pane type: ${activePane.typeId}`);
            return;
        }

        // Add/remove fill modifier based on pane type's 'fill' capability
        // This enables panes that handle their own scrolling (e.g., DataListView)
        cell.contentElement.classList.toggle(
            'cs-cell-content--fill',
            type.capabilities.includes('fill')
        );

        // Render active pane content
        let element = activePane.element;
        if (!element) {
            element = type.render(this.shell.createRenderContext(activePane, this));
            activePane.element = element;
        }
        element.style.display = '';
        cell.contentElement.appendChild(element);

        // Pre-render and keep alive inactive panes that have eagerRender set.
        // These need background initialization (e.g., camera surfaces with WebSocket connections).
        for (const pane of cell.panes) {
            if (pane === activePane) continue;
            const paneType = this.shell.getPaneType(pane.typeId);
            if (paneType?.eagerRender) {
                if (!pane.element) {
                    pane.element = paneType.render(this.shell.createRenderContext(pane, this));
                }
                pane.element.style.display = 'none';
                cell.contentElement.appendChild(pane.element);
            }
        }

        // Click to focus
        cell.contentElement.addEventListener('click', () => {
            this.shell.setFocusedPane(activePane.instanceId, this.surfaceId);
        });
    }

    private updateTabStyles(cell: GridCell): void {
        if (!cell.tabBarElement) return;

        const activePane = cell.panes[cell.activePaneIndex];
        const activeInstanceId = activePane?.instanceId;

        const tabs = cell.tabBarElement.querySelectorAll('.cs-cell-tab');
        tabs.forEach((tab) => {
            const tabInstanceId = (tab as HTMLElement).dataset.instanceId;
            tab.classList.toggle('cs-active', tabInstanceId === activeInstanceId);
        });

        this.renderTabBarActions(cell);
    }

    private isPaneRecommended(typeId: string): boolean {
        const workspace = this.shell.getWorkspaceDef(this.shell.getActiveWorkspace());
        return workspace?.recommendedPanes?.includes(typeId) ?? true;
    }

    // ========================================================================
    // Dividers
    // ========================================================================

    private renderDividers(): void {
        if (!this.gridElement) return;

        // Column dividers. One segment per contiguous run of rows where the
        // column boundary is a real cell edge — so a divider never overhangs
        // a cell that spans across it (which would intercept hover and paint
        // its highlight full-height over unrelated content).
        this.columnDividers = [];
        for (let i = 0; i < this.columnSizes.length - 1; i++) {
            let representative: HTMLElement | null = null;
            for (const [a, b] of this.dividerRuns('column', i, this.rowSizes.length)) {
                const divider = createElement('div', {
                    className: 'cs-column-divider',
                    attributes: { 'data-divider-index': String(i) },
                });
                // Divider sits in the gap column; its run spans rows a..b.
                divider.style.gridColumn = String((i + 1) * 2);
                divider.style.gridRow = `${a * 2 + 1} / ${b * 2 + 2}`;
                divider.addEventListener('mousedown', (e) => this.startColumnResize(i, e));
                this.gridElement.appendChild(divider);
                representative = representative ?? divider;
            }
            // Keep the array index aligned with the resize index (used by the
            // snap indicator); null when the boundary is a real edge nowhere.
            this.columnDividers.push(representative);
        }

        // Row dividers. One segment per contiguous run of columns where the
        // row boundary is a real cell edge (see column dividers above).
        this.rowDividers = [];
        for (let i = 0; i < this.rowSizes.length - 1; i++) {
            let representative: HTMLElement | null = null;
            for (const [a, b] of this.dividerRuns('row', i, this.columnSizes.length)) {
                const divider = createElement('div', {
                    className: 'cs-row-divider',
                    attributes: { 'data-divider-index': String(i) },
                });
                // Divider sits in the gap row; its run spans columns a..b.
                divider.style.gridColumn = `${a * 2 + 1} / ${b * 2 + 2}`;
                divider.style.gridRow = String((i + 1) * 2);
                divider.addEventListener('mousedown', (e) => this.startRowResize(i, e));
                this.gridElement.appendChild(divider);
                representative = representative ?? divider;
            }
            this.rowDividers.push(representative);
        }
    }

    // A divider track is "crossed" (not a real edge) at perpendicular index
    // p when some cell occupies both sides of the boundary AND covers p —
    // e.g. an editor cell spanning every row sits across the row boundaries
    // of a stacked sidebar. Returns the maximal contiguous [start, end]
    // (inclusive) runs of non-crossed indices, so renderDividers emits one
    // divider segment per run rather than one full-span element that
    // overhangs the spanning cell. boundaryIndex is the divider index
    // (between track boundaryIndex and boundaryIndex+1); perpCount is the
    // number of tracks on the perpendicular axis.
    private dividerRuns(
        orientation: 'row' | 'column',
        boundaryIndex: number,
        perpCount: number,
    ): Array<[number, number]> {
        const runs: Array<[number, number]> = [];
        let start = -1;
        for (let p = 0; p < perpCount; p++) {
            let crossed = false;
            for (const cell of this.cells.values()) {
                const spans = orientation === 'row'
                    ? cell.rowStart <= boundaryIndex && cell.rowEnd >= boundaryIndex + 2
                    : cell.colStart <= boundaryIndex && cell.colEnd >= boundaryIndex + 2;
                const covers = orientation === 'row'
                    ? cell.colStart <= p && cell.colEnd >= p + 1
                    : cell.rowStart <= p && cell.rowEnd >= p + 1;
                if (spans && covers) { crossed = true; break; }
            }
            if (!crossed) {
                if (start === -1) start = p;
            } else if (start !== -1) {
                runs.push([start, p - 1]);
                start = -1;
            }
        }
        if (start !== -1) runs.push([start, perpCount - 1]);
        return runs;
    }

    // ========================================================================
    // Resize Handling
    // ========================================================================

    /**
     * Calculate snap points for even distribution.
     * For N cells, snap points are at 100/N, 200/N, etc.
     * Returns cumulative positions where dividers should snap.
     */
    private calculateSnapPoints(sizes: number[]): number[] {
        const count = sizes.length;
        const evenSize = 100 / count;
        const snapPoints: number[] = [];

        // The divider at index i is between cell i and cell i+1
        // Its position is i * evenSize for even distribution
        for (let i = 1; i < count; i++) {
            snapPoints.push(i * evenSize);
        }

        return snapPoints;
    }

    private startColumnResize(index: number, e: MouseEvent): void {
        e.preventDefault();
        this.resizeState = {
            type: 'column',
            index,
            startPos: e.clientX,
            startSizes: [...this.columnSizes],
            snapPoints: this.calculateSnapPoints(this.columnSizes),
            isSnapped: false,
        };
        document.addEventListener('mousemove', this.handleResizeMove);
        document.addEventListener('mouseup', this.handleResizeEnd);
        document.body.style.cursor = 'col-resize';
    }

    private startRowResize(index: number, e: MouseEvent): void {
        e.preventDefault();
        this.resizeState = {
            type: 'row',
            index,
            startPos: e.clientY,
            startSizes: [...this.rowSizes],
            snapPoints: this.calculateSnapPoints(this.rowSizes),
            isSnapped: false,
        };
        document.addEventListener('mousemove', this.handleResizeMove);
        document.addEventListener('mouseup', this.handleResizeEnd);
        document.body.style.cursor = 'row-resize';
    }

/**
     * Check if a divider position should snap to an even-distribution point.
     * Returns the snapped position if within tolerance, otherwise the original position.
     */
    private getSnappedPosition(position: number, snapPoints: number[], index: number): { position: number; snapped: boolean } {
        if (this.snapTolerance <= 0) {
            return { position, snapped: false };
        }

        // The snap point for this divider is at snapPoints[index]
        const targetSnapPoint = snapPoints[index];
        if (targetSnapPoint === undefined) {
            return { position, snapped: false };
        }

        const distance = Math.abs(position - targetSnapPoint);
        if (distance <= this.snapTolerance) {
            return { position: targetSnapPoint, snapped: true };
        }

        return { position, snapped: false };
    }

    private showSnapIndicator(dividerElement: HTMLElement, isColumn: boolean): void {
        if (!this.snapIndicator) {
            this.snapIndicator = createElement('div', {
                className: 'cs-snap-indicator',
            });
            document.body.appendChild(this.snapIndicator);
        }

        const rect = dividerElement.getBoundingClientRect();
        this.snapIndicator.style.display = 'block';

        if (isColumn) {
            // Vertical line for column divider
            this.snapIndicator.style.left = `${rect.left + rect.width / 2 - 1}px`;
            this.snapIndicator.style.top = `${rect.top}px`;
            this.snapIndicator.style.width = '2px';
            this.snapIndicator.style.height = `${rect.height}px`;
        } else {
            // Horizontal line for row divider
            this.snapIndicator.style.left = `${rect.left}px`;
            this.snapIndicator.style.top = `${rect.top + rect.height / 2 - 1}px`;
            this.snapIndicator.style.width = `${rect.width}px`;
            this.snapIndicator.style.height = '2px';
        }
    }

    private hideSnapIndicator(): void {
        if (this.snapIndicator) {
            this.snapIndicator.style.display = 'none';
        }
    }

    private handleResizeMove = (e: MouseEvent): void => {
        if (!this.resizeState || !this.gridElement) return;

        const { type, index, startPos, startSizes, snapPoints } = this.resizeState;
        const containerRect = this.gridElement.getBoundingClientRect();

        if (type === 'column') {
            const delta = e.clientX - startPos;
            const totalWidth = containerRect.width - (this.columnSizes.length - 1) * 4; // Subtract divider widths
            const deltaPercent = (delta / totalWidth) * 100;

            // Calculate new sizes
            const newSizes = [...startSizes];
            let leftSize = startSizes[index] + deltaPercent;
            let rightSize = startSizes[index + 1] - deltaPercent;

            // Calculate the divider position (cumulative size up to this divider)
            let dividerPosition = 0;
            for (let i = 0; i <= index; i++) {
                dividerPosition += (i === index) ? leftSize : startSizes[i];
            }

            // Check for snap
            const snapResult = this.getSnappedPosition(dividerPosition, snapPoints, index);
            if (snapResult.snapped) {
                // Adjust sizes to snap position
                const adjustment = snapResult.position - dividerPosition;
                leftSize += adjustment;
                rightSize -= adjustment;
            }

            // Enforce minimum sizes
            const minPercent = (this.minCellSize / totalWidth) * 100;
            if (leftSize >= minPercent && rightSize >= minPercent) {
                newSizes[index] = leftSize;
                newSizes[index + 1] = rightSize;
                this.columnSizes = newSizes;
                this.updateGridTemplate();

                // Update snap indicator
                if (snapResult.snapped) {
                    this.resizeState.isSnapped = true;
                    const divider = this.columnDividers[index];
                    if (divider) {
                        this.showSnapIndicator(divider, true);
                    }
                } else {
                    this.resizeState.isSnapped = false;
                    this.hideSnapIndicator();
                }
            }
        } else {
            const delta = e.clientY - startPos;
            const totalHeight = containerRect.height - (this.rowSizes.length - 1) * 4;
            const deltaPercent = (delta / totalHeight) * 100;

            const newSizes = [...startSizes];
            let topSize = startSizes[index] + deltaPercent;
            let bottomSize = startSizes[index + 1] - deltaPercent;

            // Calculate the divider position (cumulative size up to this divider)
            let dividerPosition = 0;
            for (let i = 0; i <= index; i++) {
                dividerPosition += (i === index) ? topSize : startSizes[i];
            }

            // Check for snap
            const snapResult = this.getSnappedPosition(dividerPosition, snapPoints, index);
            if (snapResult.snapped) {
                // Adjust sizes to snap position
                const adjustment = snapResult.position - dividerPosition;
                topSize += adjustment;
                bottomSize -= adjustment;
            }

            const minPercent = (this.minCellSize / totalHeight) * 100;
            if (topSize >= minPercent && bottomSize >= minPercent) {
                newSizes[index] = topSize;
                newSizes[index + 1] = bottomSize;
                this.rowSizes = newSizes;
                this.updateGridTemplate();

                // Update snap indicator
                if (snapResult.snapped) {
                    this.resizeState.isSnapped = true;
                    const divider = this.rowDividers[index];
                    if (divider) {
                        this.showSnapIndicator(divider, false);
                    }
                } else {
                    this.resizeState.isSnapped = false;
                    this.hideSnapIndicator();
                }
            }
        }
    };

    private handleResizeEnd = (): void => {
        this.resizeState = null;
        document.removeEventListener('mousemove', this.handleResizeMove);
        document.removeEventListener('mouseup', this.handleResizeEnd);
        document.body.style.cursor = '';

        // Hide snap indicator
        this.hideSnapIndicator();

        // Save configuration
        this.shell.saveConfiguration();
        this.shell.emit('layout-changed', { surfaceId: this.surfaceId });
    };

    // ========================================================================
    // Drag & Drop
    // ========================================================================

    private cleanupDragHold(): void {
        if (this.dragHoldTimeout) {
            clearTimeout(this.dragHoldTimeout);
            this.dragHoldTimeout = null;
        }
        if (this.dragHoldMouseUp) {
            document.removeEventListener('mouseup', this.dragHoldMouseUp);
            this.dragHoldMouseUp = null;
        }
        if (this.dragHoldMouseMove) {
            document.removeEventListener('mousemove', this.dragHoldMouseMove);
            this.dragHoldMouseMove = null;
        }
    }

    private startDragHold(paneId: string, cellId: string, e: MouseEvent): void {
        // Clean up any existing drag hold state first
        this.cleanupDragHold();

        // Don't start if already dragging
        if (this.dragState) return;

        const startX = e.clientX;
        const startY = e.clientY;

        this.dragHoldMouseUp = () => {
            this.cleanupDragHold();
        };

        this.dragHoldMouseMove = (moveE: MouseEvent) => {
            const dx = Math.abs(moveE.clientX - startX);
            const dy = Math.abs(moveE.clientY - startY);
            if (dx > 5 || dy > 5) {
                this.cleanupDragHold();
                this.startDrag(paneId, cellId, moveE.clientX, moveE.clientY);
            }
        };

        // Start drag after hold time
        this.dragHoldTimeout = setTimeout(() => {
            this.cleanupDragHold();
            this.startDrag(paneId, cellId, startX, startY);
        }, this.DRAG_HOLD_TIME);

        document.addEventListener('mouseup', this.dragHoldMouseUp);
        document.addEventListener('mousemove', this.dragHoldMouseMove);
    }

    private startDrag(paneId: string, cellId: string, x: number, y: number): void {
        // Don't start if already dragging
        if (this.dragState) {
            console.error('[GridSurface] startDrag called while drag already in progress');
            return;
        }

        const pane = this.getPaneById(paneId);
        if (!pane) {
            console.error(`[GridSurface] startDrag: pane not found: ${paneId}`);
            return;
        }

        // Create drag ghost element
        const dragElement = createElement('div', {
            className: 'cs-drag-ghost',
            textContent: pane.title,
        });
        dragElement.style.position = 'fixed';
        dragElement.style.left = `${x}px`;
        dragElement.style.top = `${y}px`;
        dragElement.style.pointerEvents = 'none';
        document.body.appendChild(dragElement);

        this.dragState = {
            draggedPaneId: paneId,
            sourceCellId: cellId,
            dragElement,
        };

        // Add drag event listeners
        document.addEventListener('mousemove', this.handleDragMove);
        document.addEventListener('mouseup', this.handleDragEnd);

        // Add dragging class to body
        document.body.classList.add('cs-dragging');
    }

    private handleDragMove = (e: MouseEvent): void => {
        if (!this.dragState?.dragElement) return;

        // Update ghost position
        this.dragState.dragElement.style.left = `${e.clientX + 10}px`;
        this.dragState.dragElement.style.top = `${e.clientY + 10}px`;

        // Find drop target
        const target = this.findDropTarget(e.clientX, e.clientY);
        this.highlightDropTarget(target);
    };

    private handleDragEnd = (e: MouseEvent): void => {
        // Always remove event listeners first to prevent multiple calls
        document.removeEventListener('mousemove', this.handleDragMove);
        document.removeEventListener('mouseup', this.handleDragEnd);

        if (!this.dragState) return;

        // Store reference to drag element before any operations that might clear dragState
        const dragElement = this.dragState.dragElement;

        try {
            // Find drop target
            const target = this.findDropTarget(e.clientX, e.clientY);

            if (target) {
                this.handleDrop(target);
            }
        } finally {
            // Cleanup - always runs even if handleDrop throws
            if (dragElement) {
                dragElement.remove();
            }
            document.body.classList.remove('cs-dragging');
            this.clearDropHighlight();
            this.dragState = null;
        }
    };

    private findDropTarget(x: number, y: number): DropTarget | null {
        const element = document.elementFromPoint(x, y) as HTMLElement;
        if (!element) return null;

        // Find cell container
        const cellElement = findAncestorByClass(element, 'cs-grid-cell');
        if (!cellElement) return null;

        const cellId = cellElement.dataset.cellId;
        if (!cellId) return null;

        // Check if dropping on tab bar
        const tabBar = findAncestorByClass(element, 'cs-cell-tab-bar');
        if (tabBar) {
            return {
                targetType: 'tab-bar',
                surfaceId: this.surfaceId,
                cellId,
            };
        }

        // Dropping on cell body
        return {
            targetType: 'cell-body',
            surfaceId: this.surfaceId,
            cellId,
        };
    }

    private highlightDropTarget(target: DropTarget | null): void {
        this.clearDropHighlight();

        if (!target) return;

        const cell = this.cells.get(target.cellId!);
        if (cell?.element) {
            cell.element.classList.add('cs-drop-target');
        }
    }

    private clearDropHighlight(): void {
        for (const cell of this.cells.values()) {
            cell.element?.classList.remove('cs-drop-target');
        }
    }

    private handleDrop(target: DropTarget): void {
        if (!this.dragState) return;

        const { draggedPaneId, sourceCellId } = this.dragState;
        const targetCellId = target.cellId;

        if (!targetCellId || sourceCellId === targetCellId) return;

        // Find source and target cells
        const sourceCell = this.cells.get(sourceCellId);
        const targetCell = this.cells.get(targetCellId);

        if (!sourceCell || !targetCell) return;

        // Remove pane from source
        const paneIndex = sourceCell.panes.findIndex(p => p.instanceId === draggedPaneId);
        if (paneIndex < 0) return;

        const [pane] = sourceCell.panes.splice(paneIndex, 1);

        // Adjust source active index
        if (sourceCell.activePaneIndex >= sourceCell.panes.length) {
            sourceCell.activePaneIndex = Math.max(0, sourceCell.panes.length - 1);
        }

        // Add to target
        targetCell.panes.push(pane);
        targetCell.activePaneIndex = targetCell.panes.length - 1;

        // Re-render affected cells
        this.renderCellTabBar(sourceCell);
        this.renderCellContent(sourceCell);
        this.renderCellTabBar(targetCell);
        this.renderCellContent(targetCell);

        // Focus the moved pane
        this.shell.setFocusedPane(draggedPaneId, this.surfaceId);

        // Save and emit
        this.shell.saveConfiguration();
        this.shell.emit('layout-changed', { surfaceId: this.surfaceId });
    }

    // ========================================================================
    // Context Menu
    // ========================================================================

    /** Show the context menu for the first cell (used by workspace button right-click). */
    showContextMenuAt(e: MouseEvent): void {
        const firstCell = this.cells.values().next().value as GridCell | undefined;
        if (firstCell) {
            this.showContextMenu(e, firstCell);
        }
    }

    private showContextMenu(e: MouseEvent, cell: GridCell, pane?: PaneInstance): void {
        e.preventDefault();

        // Remove existing menu
        this.hideContextMenu();

        this.contextMenuElement = createElement('div', {
            className: 'cs-context-menu',
        });

        // Build menu items
        const items: Array<{
            label: string;
            action: () => void;
            disabled?: boolean;
            icon?: string;
            iconClass?: string;
            textClass?: string;
        }> = [];

        // Pane visibility section
        items.push({ label: '--- Visible Panes ---', action: () => {}, disabled: true });

        // Collect pane types currently in use in this surface (including detached)
        const currentPaneTypes = new Set<string>();
        for (const c of this.cells.values()) {
            for (const p of c.panes) {
                currentPaneTypes.add(p.typeId);
            }
        }
        for (const typeId of this.detachedPanes.keys()) {
            currentPaneTypes.add(typeId);
        }

        // Show configured pane types + any dynamic types currently in use or detached
        const allTypes = this.shell.getTypeRegistry().getAll()
            .filter(type => this.configuredPaneTypes.has(type.typeId) || currentPaneTypes.has(type.typeId));

        for (const type of allTypes) {
            const hasPaneInThisCell = cell.panes.some(p => p.typeId === type.typeId);
            const hasPaneInOtherCell = !hasPaneInThisCell && Array.from(this.cells.values())
                .some(c => c !== cell && c.panes.some(p => p.typeId === type.typeId));
            const isRecommended = this.isPaneRecommended(type.typeId);

            // Determine icon and styling
            let iconClass = 'cs-menu-icon-none';
            let icon = '';
            let textClass = 'cs-menu-text-dim';

            if (hasPaneInThisCell) {
                icon = '✓';
                iconClass = 'cs-menu-icon-active';
                textClass = 'cs-menu-text-normal';
            } else if (hasPaneInOtherCell) {
                icon = '✓';
                iconClass = 'cs-menu-icon-other';
            } else if (isRecommended) {
                icon = '⚠';
                iconClass = 'cs-menu-icon-warning';
            }

            items.push({
                label: type.defaultTitle,
                action: () => this.togglePaneInCell(cell, type.typeId),
                icon,
                iconClass,
                textClass,
            });
        }

        items.push({ label: '---', action: () => {}, disabled: true });

        // Cell operations
        items.push({
            label: 'Split Horizontally',
            action: () => this.splitCellHorizontally(cell),
        });
        items.push({
            label: 'Split Vertically',
            action: () => this.splitCellVertically(cell),
        });

        if (this.cells.size > 1) {
            items.push({
                label: 'Remove This Cell',
                action: () => this.removeCell(cell),
            });
        }

        items.push({ label: '---', action: () => {}, disabled: true });

        // Expand options
        if (this.canExpandRight(cell)) {
            items.push({ label: 'Expand to Right', action: () => this.expandRight(cell) });
        }
        if (this.canExpandLeft(cell)) {
            items.push({ label: 'Expand to Left', action: () => this.expandLeft(cell) });
        }
        if (this.canExpandDown(cell)) {
            items.push({ label: 'Expand Down', action: () => this.expandDown(cell) });
        }
        if (this.canExpandUp(cell)) {
            items.push({ label: 'Expand Up', action: () => this.expandUp(cell) });
        }

        // Remove surface (for container panes like camera-surface)
        if (pane) {
            const paneType = this.shell.getTypeRegistry().get(pane.typeId);
            if (paneType && paneType.kind === 'container') {
                items.push({ label: '---', action: () => {}, disabled: true });
                items.push({
                    label: 'Remove',
                    action: async () => {
                        if (!await this.shouldClosePane(pane)) return;
                        this.closePane(pane.instanceId);
                        // Auto-remove empty cell
                        if (cell.panes.length === 0 && this.cells.size > 1) {
                            this.removeCell(cell);
                        }
                    },
                    icon: '✕',
                    iconClass: 'cs-menu-icon-danger',
                    textClass: 'cs-menu-text-danger',
                });
            }
        }

        // Render menu items
        for (const item of items) {
            if (item.label === '---') {
                const sep = createElement('div', { className: 'cs-menu-separator' });
                this.contextMenuElement.appendChild(sep);
            } else {
                const classes = ['cs-menu-item'];
                if (item.disabled) classes.push('cs-disabled');

                const menuItem = createElement('div', {
                    className: classes.join(' '),
                    onClick: (e) => {
                        e.stopPropagation();
                        if (!item.disabled) {
                            item.action();
                            this.hideContextMenu();
                        }
                    },
                });

                // Add icon span if present
                if (item.icon) {
                    const iconSpan = createElement('span', {
                        className: `cs-menu-icon ${item.iconClass || ''}`,
                        textContent: item.icon,
                    });
                    menuItem.appendChild(iconSpan);
                } else {
                    // Empty space for alignment
                    const iconSpan = createElement('span', {
                        className: 'cs-menu-icon cs-menu-icon-none',
                        textContent: ' ',
                    });
                    menuItem.appendChild(iconSpan);
                }

                // Add label span
                const labelSpan = createElement('span', {
                    className: `cs-menu-label ${item.textClass || ''}`,
                    textContent: item.label,
                });
                menuItem.appendChild(labelSpan);

                this.contextMenuElement.appendChild(menuItem);
            }
        }

        // Position menu
        this.contextMenuElement.style.position = 'fixed';
        this.contextMenuElement.style.left = `${e.clientX}px`;
        this.contextMenuElement.style.top = `${e.clientY}px`;

        document.body.appendChild(this.contextMenuElement);

        // Close on outside click
        setTimeout(() => {
            document.addEventListener('click', this.handleContextMenuClose);
        }, 0);
    }

    private handleContextMenuClose = (): void => {
        this.hideContextMenu();
    };

    private hideContextMenu(): void {
        if (this.contextMenuElement) {
            this.contextMenuElement.remove();
            this.contextMenuElement = null;
            document.removeEventListener('click', this.handleContextMenuClose);
        }
    }

    // ========================================================================
    // Cell Operations
    // ========================================================================

    private togglePaneInCell(cell: GridCell, typeId: string): void {
        const existingIndex = cell.panes.findIndex(p => p.typeId === typeId);

        if (existingIndex >= 0) {
            // Remove pane (even if it's the last one — cell becomes empty)
            const [removed] = cell.panes.splice(existingIndex, 1);
            if (cell.activePaneIndex >= cell.panes.length) {
                cell.activePaneIndex = Math.max(0, cell.panes.length - 1);
            }
            // Stash for re-attachment (keeps subscriptions alive)
            removed.isDetached = true;
            this.shell.getPaneType(removed.typeId)?.onDetach?.(removed);
            this.detachedPanes.set(removed.typeId, removed);
            this.detachedSourceCells.set(removed.typeId, cell.cellId);
            this.renderCellTabBar(cell);
            this.renderCellContent(cell);
        } else {
            // Check detached stash first (preserves live state)
            let instance = this.detachedPanes.get(typeId);
            if (instance) {
                this.detachedPanes.delete(typeId);
                this.detachedSourceCells.delete(typeId);
                instance.isDetached = false;
                this.shell.getPaneType(instance.typeId)?.onAttach?.(instance);
                cell.panes.push(instance);
                cell.activePaneIndex = cell.panes.length - 1;
                // Close the orphaned popout window (if any) that held a copy of this pane
                this.shell.getPopoutManager().closePopoutByPaneType(typeId);
            } else {
                // Check if this pane type exists in another cell and move it
                let movedInstance: PaneInstance | null = null;
                let sourceCell: GridCell | null = null;

                for (const otherCell of this.cells.values()) {
                    if (otherCell.cellId === cell.cellId) continue;

                    const paneIndex = otherCell.panes.findIndex(p => p.typeId === typeId);
                    if (paneIndex >= 0) {
                        // Found the pane in another cell - remove it
                        [movedInstance] = otherCell.panes.splice(paneIndex, 1);
                        sourceCell = otherCell;

                        // Adjust source cell's active index
                        if (otherCell.activePaneIndex >= otherCell.panes.length) {
                            otherCell.activePaneIndex = Math.max(0, otherCell.panes.length - 1);
                        }
                        break;
                    }
                }

                if (movedInstance) {
                    // Move existing instance to this cell
                    cell.panes.push(movedInstance);
                    cell.activePaneIndex = cell.panes.length - 1;

                    // Re-render source cell
                    if (sourceCell) {
                        this.renderCellTabBar(sourceCell);
                        this.renderCellContent(sourceCell);
                    }
                } else {
                    // Create new pane instance
                    instance = this.shell.getTypeRegistry().createInstance({ typeId });
                    instance.parentSurfaceId = this.surfaceId;
                    this.shell.registerPaneInstance(instance);
                    cell.panes.push(instance);
                    cell.activePaneIndex = cell.panes.length - 1;
                }
            }

            this.renderCellTabBar(cell);
            this.renderCellContent(cell);
        }

        this.shell.saveConfiguration();
    }

    /**
     * Re-attach a detached pane back into its source cell (or the best available cell).
     * Called by PopoutManager when a popout window is closed.
     * @param storedSourceCellId Optional persisted source cell ID (survives page reloads).
     */
    reattachDetachedPane(typeId: string, storedSourceCellId?: string): void {
        // Use in-memory detached instance if available, otherwise create fresh
        let instance = this.detachedPanes.get(typeId);
        if (instance) {
            this.detachedPanes.delete(typeId);
            instance.isDetached = false;
            this.shell.getPaneType(instance.typeId)?.onAttach?.(instance);
        } else {
            // After a page reload the detached stash is gone — create a new instance
            instance = this.shell.getTypeRegistry().createInstance({ typeId });
            instance.parentSurfaceId = this.surfaceId;
            this.shell.registerPaneInstance(instance);
        }

        // Prefer in-memory source cell, then persisted source cell, then fewest-panes cell
        const sourceCellId = this.detachedSourceCells.get(typeId) ?? storedSourceCellId;
        this.detachedSourceCells.delete(typeId);

        let targetCell = sourceCellId ? this.cells.get(sourceCellId) : undefined;
        if (!targetCell) {
            let fewest = Infinity;
            for (const c of this.cells.values()) {
                if (c.panes.length < fewest) {
                    fewest = c.panes.length;
                    targetCell = c;
                }
            }
        }

        if (!targetCell) return;

        targetCell.panes.push(instance);
        targetCell.activePaneIndex = targetCell.panes.length - 1;
        this.renderCellTabBar(targetCell);
        this.renderCellContent(targetCell);
        this.shell.saveConfiguration();
    }

    private splitCellHorizontally(cell: GridCell): void {
        // Add a new row and split this cell
        const newRowIndex = cell.rowEnd;

        // Insert new row
        const cellRowSpan = cell.rowEnd - cell.rowStart;
        const splitHeight = this.rowSizes[cell.rowStart] / 2;

        // Insert a new row size
        this.rowSizes.splice(newRowIndex, 0, splitHeight);
        this.rowSizes[cell.rowStart] = splitHeight;

        // Update all cells that are at or past the split point
        for (const otherCell of this.cells.values()) {
            if (otherCell.rowStart >= newRowIndex) {
                otherCell.rowStart++;
            }
            if (otherCell.rowEnd > newRowIndex) {
                otherCell.rowEnd++;
            }
        }

        // Create new cell below
        const newCell: GridCell = {
            cellId: generateId('cell'),
            rowStart: newRowIndex,
            rowEnd: newRowIndex + 1,
            colStart: cell.colStart,
            colEnd: cell.colEnd,
            panes: [],
            activePaneIndex: -1,
        };

        cell.rowEnd = newRowIndex;
        this.cells.set(newCell.cellId, newCell);

        // Re-render
        this.render();
        this.shell.saveConfiguration();
    }

    private splitCellVertically(cell: GridCell): void {
        const newColIndex = cell.colEnd;

        const splitWidth = this.columnSizes[cell.colStart] / 2;

        this.columnSizes.splice(newColIndex, 0, splitWidth);
        this.columnSizes[cell.colStart] = splitWidth;

        // Update all cells
        for (const otherCell of this.cells.values()) {
            if (otherCell.colStart >= newColIndex) {
                otherCell.colStart++;
            }
            if (otherCell.colEnd > newColIndex) {
                otherCell.colEnd++;
            }
        }

        // Create new cell to the right
        const newCell: GridCell = {
            cellId: generateId('cell'),
            rowStart: cell.rowStart,
            rowEnd: cell.rowEnd,
            colStart: newColIndex,
            colEnd: newColIndex + 1,
            panes: [],
            activePaneIndex: -1,
        };

        cell.colEnd = newColIndex;
        this.cells.set(newCell.cellId, newCell);

        // Re-render
        this.render();
        this.shell.saveConfiguration();
    }

    private removeCell(cell: GridCell): void {
        // Find a neighbor to absorb this cell
        const neighbor = this.findAbsorbingNeighbor(cell);
        if (!neighbor) return;

        // Expand neighbor to absorb this cell
        if (neighbor.rowStart > cell.rowStart) {
            neighbor.rowStart = cell.rowStart;
        }
        if (neighbor.rowEnd < cell.rowEnd) {
            neighbor.rowEnd = cell.rowEnd;
        }
        if (neighbor.colStart > cell.colStart) {
            neighbor.colStart = cell.colStart;
        }
        if (neighbor.colEnd < cell.colEnd) {
            neighbor.colEnd = cell.colEnd;
        }

        // Move panes to neighbor
        neighbor.panes.push(...cell.panes);
        if (neighbor.activePaneIndex < 0 && neighbor.panes.length > 0) {
            neighbor.activePaneIndex = 0;
        }

        // Remove cell
        this.cells.delete(cell.cellId);

        // Re-render
        this.render();
        this.shell.saveConfiguration();
    }

    private findAbsorbingNeighbor(cell: GridCell): GridCell | null {
        // Try to find a neighbor that can absorb this cell
        for (const neighbor of this.cells.values()) {
            if (neighbor.cellId === cell.cellId) continue;

            // Check if neighbor is directly above
            if (neighbor.rowEnd === cell.rowStart &&
                neighbor.colStart === cell.colStart &&
                neighbor.colEnd === cell.colEnd) {
                return neighbor;
            }
            // Check if neighbor is directly below
            if (neighbor.rowStart === cell.rowEnd &&
                neighbor.colStart === cell.colStart &&
                neighbor.colEnd === cell.colEnd) {
                return neighbor;
            }
            // Check if neighbor is directly left
            if (neighbor.colEnd === cell.colStart &&
                neighbor.rowStart === cell.rowStart &&
                neighbor.rowEnd === cell.rowEnd) {
                return neighbor;
            }
            // Check if neighbor is directly right
            if (neighbor.colStart === cell.colEnd &&
                neighbor.rowStart === cell.rowStart &&
                neighbor.rowEnd === cell.rowEnd) {
                return neighbor;
            }
        }
        return null;
    }

    // ========================================================================
    // Expand Operations
    // ========================================================================

    private canExpandRight(cell: GridCell): boolean {
        return this.findNeighborRight(cell) !== null;
    }

    private canExpandLeft(cell: GridCell): boolean {
        return this.findNeighborLeft(cell) !== null;
    }

    private canExpandDown(cell: GridCell): boolean {
        return this.findNeighborBelow(cell) !== null;
    }

    private canExpandUp(cell: GridCell): boolean {
        return this.findNeighborAbove(cell) !== null;
    }

    private findNeighborRight(cell: GridCell): GridCell | null {
        for (const neighbor of this.cells.values()) {
            if (neighbor.colStart === cell.colEnd &&
                neighbor.rowStart === cell.rowStart &&
                neighbor.rowEnd === cell.rowEnd) {
                return neighbor;
            }
        }
        return null;
    }

    private findNeighborLeft(cell: GridCell): GridCell | null {
        for (const neighbor of this.cells.values()) {
            if (neighbor.colEnd === cell.colStart &&
                neighbor.rowStart === cell.rowStart &&
                neighbor.rowEnd === cell.rowEnd) {
                return neighbor;
            }
        }
        return null;
    }

    private findNeighborBelow(cell: GridCell): GridCell | null {
        for (const neighbor of this.cells.values()) {
            if (neighbor.rowStart === cell.rowEnd &&
                neighbor.colStart === cell.colStart &&
                neighbor.colEnd === cell.colEnd) {
                return neighbor;
            }
        }
        return null;
    }

    private findNeighborAbove(cell: GridCell): GridCell | null {
        for (const neighbor of this.cells.values()) {
            if (neighbor.rowEnd === cell.rowStart &&
                neighbor.colStart === cell.colStart &&
                neighbor.colEnd === cell.colEnd) {
                return neighbor;
            }
        }
        return null;
    }

    private expandRight(cell: GridCell): void {
        const neighbor = this.findNeighborRight(cell);
        if (!neighbor) return;
        this.absorbCell(cell, neighbor);
    }

    private expandLeft(cell: GridCell): void {
        const neighbor = this.findNeighborLeft(cell);
        if (!neighbor) return;
        this.absorbCell(cell, neighbor);
    }

    private expandDown(cell: GridCell): void {
        const neighbor = this.findNeighborBelow(cell);
        if (!neighbor) return;
        this.absorbCell(cell, neighbor);
    }

    private expandUp(cell: GridCell): void {
        const neighbor = this.findNeighborAbove(cell);
        if (!neighbor) return;
        this.absorbCell(cell, neighbor);
    }

    private absorbCell(absorber: GridCell, absorbed: GridCell): void {
        // Expand absorber to cover absorbed cell
        absorber.rowStart = Math.min(absorber.rowStart, absorbed.rowStart);
        absorber.rowEnd = Math.max(absorber.rowEnd, absorbed.rowEnd);
        absorber.colStart = Math.min(absorber.colStart, absorbed.colStart);
        absorber.colEnd = Math.max(absorber.colEnd, absorbed.colEnd);

        // Move panes
        absorber.panes.push(...absorbed.panes);

        // Remove absorbed cell
        this.cells.delete(absorbed.cellId);

        // Re-render
        this.render();
        this.shell.saveConfiguration();
    }

    // ========================================================================
    // Utility
    // ========================================================================

    /** The cell holding a pane, if it is in this surface. */
    private findCellForPane(instanceId: string): GridCell | undefined {
        for (const cell of this.cells.values()) {
            if (cell.panes.some(p => p.instanceId === instanceId)) return cell;
        }
        return undefined;
    }

    /** The nearest cell that starts where this one ends, on the same row band. */
    private findCellRightOf(cell: GridCell): GridCell | undefined {
        let best: GridCell | undefined;
        for (const other of this.cells.values()) {
            if (other === cell || other.colStart < cell.colEnd) continue;
            const sameBand = other.rowStart < cell.rowEnd && other.rowEnd > cell.rowStart;
            if (!sameBand) continue;
            if (!best || other.colStart < best.colStart) best = other;
        }
        return best;
    }

    private findCellAtPosition(pos: CellPosition): GridCell | undefined {
        for (const cell of this.cells.values()) {
            if (pos.row >= cell.rowStart && pos.row < cell.rowEnd &&
                pos.col >= cell.colStart && pos.col < cell.colEnd) {
                return cell;
            }
        }
        return undefined;
    }

    private setupEventListeners(): void {
        // Global escape to close context menu
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.hideContextMenu();
            }
        });
    }

    // ========================================================================
    // Export Layout Config
    // ========================================================================

    getLayoutConfig(): GridLayoutConfig {
        const cells: GridCellConfig[] = [];

        for (const cell of this.cells.values()) {
            cells.push({
                cellId: cell.cellId,
                rowStart: cell.rowStart,
                rowEnd: cell.rowEnd,
                colStart: cell.colStart,
                colEnd: cell.colEnd,
                panes: cell.panes.map(p => {
                    const paneSpec: PaneInstanceSpec = {
                        typeId: p.typeId,
                        title: p.title,
                        context: p.context,
                        flags: p.flags,
                        isDynamic: p.isDynamic || undefined,
                    };

                    // For container panes, recursively capture their internal layout
                    const paneType = this.shell.getPaneType(p.typeId);
                    if (paneType && paneType.kind === 'container') {
                        const nestedSurface = this.shell.getSurface(p.instanceId);
                        if (nestedSurface && 'getLayoutConfig' in nestedSurface) {
                            paneSpec.nestedLayoutConfig = (nestedSurface as GridSurface).getLayoutConfig();
                        }
                    }

                    return paneSpec;
                }),
                activePaneIndex: cell.activePaneIndex,
            });
        }

        return {
            type: 'grid',
            columnCount: this.columnCount,
            columnSizes: [...this.columnSizes],
            rowSizes: [...this.rowSizes],
            cells,
        };
    }

    // ========================================================================
    // Cleanup
    // ========================================================================

    destroy(): void {
        this.hideContextMenu();
        this.hideModal();

        // Remove snap indicator if it exists
        if (this.snapIndicator) {
            this.snapIndicator.remove();
            this.snapIndicator = null;
        }

        // Clean up any pending drag hold
        this.cleanupDragHold();

        // Clean up any in-progress drag
        if (this.dragState) {
            if (this.dragState.dragElement) {
                this.dragState.dragElement.remove();
            }
            document.removeEventListener('mousemove', this.handleDragMove);
            document.removeEventListener('mouseup', this.handleDragEnd);
            document.body.classList.remove('cs-dragging');
            this.dragState = null;
        }

        // Clean up any in-progress resize
        if (this.resizeState) {
            document.removeEventListener('mousemove', this.handleResizeMove);
            document.removeEventListener('mouseup', this.handleResizeEnd);
            document.body.style.cursor = '';
            this.hideSnapIndicator();
            this.resizeState = null;
        }

        // Unregister all panes
        for (const cell of this.cells.values()) {
            for (const pane of cell.panes) {
                this.shell.unregisterPaneInstance(pane.instanceId);
            }
        }

        // Unregister surface
        this.shell.unregisterSurface(this.surfaceId);

        // Clear DOM
        clearElement(this.container);
    }
}

// ============================================================================
// Grid Surface Pane Type
// ============================================================================

export function createGridSurfacePaneType(): PaneType {
    return {
        typeId: 'grid-surface',
        kind: 'container',
        containerLayout: 'grid',
        defaultTitle: 'Grid Surface',
        capabilities: ['host'],
        defaultFlags: {
            reparentable: false,
            cloneable: false,
        },
        render: (ctx: PaneTypeRenderContext): HTMLElement => {
            const container = createElement('div', {
                className: 'cs-grid-surface-container',
            });

            // Create the GridSurface instance
            const shell = ctx.shell as Shell;
            const surface = new GridSurface(
                shell,
                ctx.instance,
                container
            );

            // Check for stored layout config (persisted from previous session)
            const workspaceId = shell.getActiveWorkspace();
            const storedLayout = shell.getStoredLayoutConfig(workspaceId);

            // Always resolve configuredPaneTypes from the workspace definition (never from storage)
            const workspace = shell.getWorkspaceDef(workspaceId);
            const screenSize = shell.getScreenSize();
            const defaultLayout = workspace?.layouts?.[screenSize] ?? workspace?.rootLayoutConfig;
            const configuredPaneTypes = (defaultLayout?.type === 'grid')
                ? (defaultLayout as GridLayoutConfig).configuredPaneTypes
                : undefined;

            if (storedLayout && storedLayout.type === 'grid') {
                // Restore from stored layout, injecting configuredPaneTypes from workspace definition
                const restored = { ...storedLayout, configuredPaneTypes } as GridLayoutConfig;
                surface.initializeLayout(restored);
            } else {

                // Try layouts[screenSize] first, then fall back to rootLayoutConfig
                const layoutForSize = workspace?.layouts?.[screenSize];
                const layoutConfig = layoutForSize ?? workspace?.rootLayoutConfig;

                if (layoutConfig && layoutConfig.type === 'grid') {
                    // Use workspace's layout for this screen size
                    surface.initializeLayout(layoutConfig as GridLayoutConfig);
                } else {
                    // Fall back to single cell with recommended panes
                    const recommendedPanes = workspace?.recommendedPanes ?? [];

                    // Build initial pane specs for the first cell
                    const initialPaneSpecs: PaneInstanceSpec[] = [];
                    for (const typeId of recommendedPanes) {
                        // Only add leaf panes (not containers)
                        const paneType = shell.getPaneType(typeId);
                        if (paneType && paneType.kind === 'leaf') {
                            initialPaneSpecs.push({ typeId });
                        }
                    }

                    // Initialize with default layout containing recommended panes
                    const defaultConfig: GridLayoutConfig = {
                        type: 'grid',
                        columnCount: 1,
                        columnSizes: [100],
                        rowSizes: [100],
                        cells: [{
                            cellId: generateId('cell'),
                            rowStart: 0,
                            rowEnd: 1,
                            colStart: 0,
                            colEnd: 1,
                            panes: initialPaneSpecs,
                            activePaneIndex: initialPaneSpecs.length > 0 ? 0 : -1,
                        }],
                    };

                    surface.initializeLayout(defaultConfig);
                }
            }

            return container;
        },
    };
}
